import { useState, useRef, useEffect } from "react";

const SEARCH_URL    = "https://zjkzrobirkkrlmrfwshw.supabase.co/functions/v1/hiring-manager-search";
const CALLBACK_BASE = "https://zjkzrobirkkrlmrfwshw.supabase.co/functions/v1/hiring-manager-callback";

const STAGES = [
  { label: "Scanning job posting...",       until: 12 },
  { label: "Finding decision-makers...",    until: 35 },
  { label: "Searching company profiles...", until: 58 },
  { label: "Verifying contacts...",         until: 78 },
  { label: "Compiling results...",          until: 90 },
  { label: "Almost there...",              until: 100 },
];

export interface Contact {
  rank: number;
  full_name: string;
  title: string;
  linkedin_url?: string;
  summary?: string;
  profile_summary?: string;
  outreach_tip?: string;
}

export interface HiringManagerResult {
  status?: string;
  job_title?: string;
  company?: string;
  location?: string;
  contacts: Contact[];
}

export function useHiringManagerSearch() {
  const [loading,       setLoading]       = useState(false);
  const [queued,        setQueued]        = useState(false);
  const [result,        setResult]        = useState<HiringManagerResult | null>(null);
  const [error,         setError]         = useState<string | null>(null);
  const [progress,      setProgress]      = useState(0);
  const [progressStage, setProgressStage] = useState("");

  const pollRef      = useRef<ReturnType<typeof setInterval> | null>(null);
  const progRef      = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef(0);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (progRef.current) clearInterval(progRef.current);
  }, []);

  const startProgress = () => {
    startedAtRef.current = Date.now();
    setProgress(0);
    setProgressStage(STAGES[0].label);
    progRef.current = setInterval(() => {
      const elapsed = (Date.now() - startedAtRef.current) / 1000;
      const p = Math.min(92 * (1 - Math.exp(-elapsed / 120)), 92);
      setProgress(Math.round(p));
      const stage = STAGES.find(s => p < s.until) ?? STAGES[STAGES.length - 1];
      setProgressStage(stage.label);
    }, 250);
  };

  const completeProgress = () => {
    if (progRef.current) clearInterval(progRef.current);
    setProgress(100);
    setProgressStage("Done!");
    setTimeout(() => { setProgress(0); setProgressStage(""); }, 600);
  };

  const search = async (query: string) => {
    if (!query.trim()) return;
    if (pollRef.current) clearInterval(pollRef.current);
    if (progRef.current) clearInterval(progRef.current);
    setLoading(true);
    setResult(null);
    setError(null);
    startProgress();

    const jobId = crypto.randomUUID();
    const isUrl = query.trim().startsWith("http");
    const payload: Record<string, string> = isUrl
      ? { job_url: query.trim() }
      : { job_description: query.trim() };
    payload.job_id       = jobId;
    payload.callback_url = `${CALLBACK_BASE}?job_id=${jobId}`;

    try {
      const res = await fetch(SEARCH_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        if (progRef.current) clearInterval(progRef.current);
        setLoading(false);
        setProgress(0);
        setProgressStage("");
        setError(data.error ?? "Could not queue the search. Please try again.");
        return;
      }
      setQueued(true);
    } catch {
      if (progRef.current) clearInterval(progRef.current);
      setLoading(false);
      setProgress(0);
      setProgressStage("");
      setError("Could not reach the search service. Please try again.");
      return;
    }

    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > 150) { // ~10 min at 4s intervals
        clearInterval(pollRef.current!);
        if (progRef.current) clearInterval(progRef.current);
        setLoading(false);
        setProgress(0);
        setProgressStage("");
        setError("Search timed out after 5 minutes — please try again.");
        return;
      }
      try {
        const res  = await fetch(`${SEARCH_URL}?job_id=${encodeURIComponent(jobId)}`);
        const data = await res.json();
        if (data && data.status !== "pending") {
          clearInterval(pollRef.current!);
          completeProgress();
          setTimeout(() => {
            setResult(data as HiringManagerResult);
            setLoading(false);
          }, 400);
        }
      } catch { /* keep polling */ }
    }, 4000);
  };

  const reset = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (progRef.current) clearInterval(progRef.current);
    setResult(null);
    setError(null);
    setLoading(false);
    setQueued(false);
    setProgress(0);
    setProgressStage("");
  };

  return { loading, queued, result, error, progress, progressStage, startedAt: startedAtRef.current || undefined, search, reset };
}
